/**
 * Admin user detail page for /admin/users/:userId route.
 * Shows user details and allows editing.
 */

import { useState } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { adminGetUserDetail, adminUpdateUser, type AdminUser } from "../services/naraDoor";
import { adminRoutes, publicRoutes } from "../lib/routes";
import { normalizePhoneNumber } from "../lib/phone";
import { WizardEntriesList } from "../components/admin/WizardEntriesList";
import { useAdminUserWorkspaces, useAdminPurgeUser } from "../hooks/useAdminPurgeUser";
import {
  Users,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Mail,
  Phone,
  Globe,
  Clock,
  Pencil,
  Save,
  X,
  AlertTriangle,
  Trash2,
  Loader2,
  CheckCircle2,
  Building2,
} from "lucide-react";

export function AdminUserDetail() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const { userId } = useParams<{ userId: string }>();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editLocale, setEditLocale] = useState("");
  const [editTimezone, setEditTimezone] = useState("");

  // Purge dialog state
  const [purgeDialogStep, setPurgeDialogStep] = useState<0 | 1 | 2 | 3>(0); // 0=closed, 1=warning, 2=email, 3=final
  const [purgeEmailInput, setPurgeEmailInput] = useState("");
  const [purgeConfirmInput, setPurgeConfirmInput] = useState("");
  const [purgeResult, setPurgeResult] = useState<{ ok: boolean; steps: Record<string, { ok: boolean; detail?: string } | undefined> } | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const {
    data: user,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["adminUser", userId],
    queryFn: () => adminGetUserDetail(userId!),
    enabled: isAdmin && !!userId,
  });

  // Fetch user's workspace memberships for the eligibility gate.
  // Always fetch when the page is loaded — `user.workspace_count` is unreliable
  // (only populated by some endpoints), so derive eligibility from the actual list.
  const {
    data: userWorkspacesData,
    isLoading: isLoadingWorkspaces,
  } = useAdminUserWorkspaces(isAdmin && userId && user ? userId : undefined);

  const updateMutation = useMutation({
    mutationFn: (data: { first_name?: string; last_name?: string; email?: string; phone?: string; locale?: string; timezone?: string }) =>
      adminUpdateUser(userId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUser", userId] });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      setIsEditing(false);
    },
  });

  const purgeMutation = useAdminPurgeUser();

  const handleStartEdit = () => {
    setEditFirstName(user?.first_name || "");
    setEditLastName(user?.last_name || "");
    setEditEmail(user?.email || "");
    setEditPhone(user?.phone || "");
    setEditLocale(user?.locale || "");
    setEditTimezone(user?.timezone || "");
    setIsEditing(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      first_name: editFirstName || undefined,
      last_name: editLastName || undefined,
      email: editEmail || undefined,
      phone: editPhone ? normalizePhoneNumber(editPhone) : undefined,
      locale: editLocale || undefined,
      timezone: editTimezone || undefined,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const getUserDisplayName = (u: AdminUser) => {
    if (u.first_name || u.last_name) {
      return `${u.first_name || ""} ${u.last_name || ""}`.trim();
    }
    return u.email || u.phone || t("adminUsers.unknown");
  };

  // ─── Purge eligibility ───────────────────────────────────────────────────────

  const eligibilityReasons: string[] = [];

  if (user && userWorkspacesData) {
    const workspaces = userWorkspacesData.workspaces;
    const wsCount = workspaces.length;

    if (wsCount === 0) {
      // Orphan user with no workspace — purge user only (no workspace cleanup).
      // No eligibility reason needed; backend handles the no-workspace path.
    } else if (wsCount > 1) {
      eligibilityReasons.push(`User is a member of ${wsCount} workspaces (must be exactly 1)`);
    } else {
      // Exactly one workspace — validate role and collaborator count
      const ws = workspaces[0];
      if (ws.role !== "owner") {
        eligibilityReasons.push(`User's role in workspace "${ws.name}" is "${ws.role ?? "none"}" (must be owner)`);
      }
      if (ws.member_count > 1) {
        eligibilityReasons.push(`Workspace "${ws.name}" has ${ws.member_count} members (must be exactly 1)`);
      }
    }

    if (!user.email) {
      eligibilityReasons.push("User has no email address on file (required for confirmation)");
    }
  }

  const isEligibleForPurge =
    user !== undefined &&
    eligibilityReasons.length === 0 &&
    !isLoadingWorkspaces;

  const purgeWorkspace = userWorkspacesData?.workspaces[0];

  // ─── Purge handlers ──────────────────────────────────────────────────────────

  const handleOpenPurgeDialog = () => {
    setPurgeDialogStep(1);
    setPurgeEmailInput("");
    setPurgeConfirmInput("");
    setPurgeResult(null);
    setPurgeError(null);
    purgeMutation.reset();
  };

  const handleClosePurgeDialog = () => {
    if (purgeMutation.isPending) return;
    setPurgeDialogStep(0);
  };

  const emailMatches =
    purgeEmailInput.trim().toLowerCase() === (user?.email ?? "").toLowerCase();

  const confirmPhraseMatches = purgeConfirmInput.trim() === "DELETE PERMANENTLY";

  const handlePurgeSubmit = async () => {
    if (!user) return;
    setPurgeError(null);
    try {
      const result = await purgeMutation.mutateAsync({
        userId: user.id,
        workspaceId: purgeWorkspace?.id,
        // Never log the email value; pass it to the callable only
        confirmEmail: purgeEmailInput.trim(),
      });
      setPurgeResult({ ok: result.ok, steps: result.steps });
      setPurgeDialogStep(0);
      // Redirect to admin users list after a brief moment to show the result banner
      setTimeout(() => {
        navigate(adminRoutes.users);
      }, 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Purge failed";
      // Log only opaque IDs, not PII
      console.error("[adminPurgeUser] failed", { userId: user.id, workspaceId: purgeWorkspace?.id ?? null });
      setPurgeError(msg);
    }
  };

  return (
    <div>
      <PageHeader
        title={user ? getUserDisplayName(user) : t("adminUsers.detail.title")}
        description={user?.email || t("adminUsers.detail.loading")}
        isAdmin
        breadcrumbs={[
          { label: t("adminUsers.breadcrumb.dashboard"), href: publicRoutes.home },
          { label: t("adminUsers.breadcrumb.admin") },
          { label: t("adminUsers.breadcrumb.users"), href: adminRoutes.users },
          { label: user ? getUserDisplayName(user) : t("adminUsers.breadcrumb.user") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={adminRoutes.users}
              className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("adminUsers.detail.back")}
            </Link>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              {t("adminUsers.refresh")}
            </button>
          </div>
        }
      />

      {/* Purge success banner */}
      {purgeResult && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-green-800 dark:text-green-200">
                User permanently deleted. Redirecting to users list…
              </p>
              <div className="mt-2 space-y-1">
                {Object.entries(purgeResult.steps).map(([step, result]) => {
                  if (!result) return null;
                  return (
                    <div key={step} className="flex items-center gap-2 text-sm">
                      {result.ok ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      ) : (
                        <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      )}
                      <span className={result.ok ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"}>
                        {step}: {result.ok ? "ok" : (result.detail ?? "failed")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800 dark:text-red-200">{t("adminUsers.error.loadUser")}</p>
            <p className="text-sm text-red-600">{(error as Error).message}</p>
          </div>
        </div>
      )}

      {/* Loading state / User details */}
      {isLoading ? (
        <div className="space-y-4">
          <div className="h-10 w-48 bg-muted rounded animate-pulse" />
          <div className="h-6 w-96 bg-muted rounded animate-pulse" />
          <div className="h-64 bg-muted rounded animate-pulse" />
        </div>
      ) : user && (
        <>
        <div className="bg-card rounded-lg border border-border">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <Users className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {getUserDisplayName(user)}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("adminUsers.detail.created", { date: formatDate(user.created_at, "date") })}
                </p>
              </div>
            </div>
            {!isEditing && (
              <button
                onClick={handleStartEdit}
                className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
              >
                <Pencil className="h-4 w-4" />
                {t("adminUsers.detail.edit")}
              </button>
            )}
          </div>

          <div className="p-6">
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("adminUsers.detail.firstName")}
                    </label>
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("adminUsers.detail.lastName")}
                    </label>
                    <input
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("adminUsers.detail.email")}
                  </label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("adminUsers.detail.phone")}
                  </label>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("adminUsers.detail.locale")}
                    </label>
                    <select
                      value={editLocale}
                      onChange={(e) => setEditLocale(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t("adminUsers.detail.selectLocale")}</option>
                      <option value="en-CA">{t("adminUsers.detail.locales.enCA")}</option>
                      <option value="en-US">{t("adminUsers.detail.locales.enUS")}</option>
                      <option value="fr-CA">{t("adminUsers.detail.locales.frCA")}</option>
                      <option value="es-US">{t("adminUsers.detail.locales.esUS")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("adminUsers.detail.timezone")}
                    </label>
                    <select
                      value={editTimezone}
                      onChange={(e) => setEditTimezone(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t("adminUsers.detail.selectTimezone")}</option>
                      <optgroup label={t("adminUsers.detail.timezones.canada")}>
                        <option value="America/Toronto">{t("adminUsers.detail.timezones.toronto")}</option>
                        <option value="America/Montreal">{t("adminUsers.detail.timezones.montreal")}</option>
                        <option value="America/Halifax">{t("adminUsers.detail.timezones.halifax")}</option>
                        <option value="America/Winnipeg">{t("adminUsers.detail.timezones.winnipeg")}</option>
                        <option value="America/Regina">{t("adminUsers.detail.timezones.regina")}</option>
                        <option value="America/Edmonton">{t("adminUsers.detail.timezones.edmonton")}</option>
                        <option value="America/Vancouver">{t("adminUsers.detail.timezones.vancouver")}</option>
                        <option value="America/St_Johns">{t("adminUsers.detail.timezones.stJohns")}</option>
                      </optgroup>
                      <optgroup label={t("adminUsers.detail.timezones.unitedStates")}>
                        <option value="America/New_York">{t("adminUsers.detail.timezones.newYork")}</option>
                        <option value="America/Chicago">{t("adminUsers.detail.timezones.chicago")}</option>
                        <option value="America/Denver">{t("adminUsers.detail.timezones.denver")}</option>
                        <option value="America/Phoenix">{t("adminUsers.detail.timezones.phoenix")}</option>
                        <option value="America/Los_Angeles">{t("adminUsers.detail.timezones.losAngeles")}</option>
                        <option value="America/Anchorage">{t("adminUsers.detail.timezones.anchorage")}</option>
                        <option value="Pacific/Honolulu">{t("adminUsers.detail.timezones.honolulu")}</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {updateMutation.isPending ? t("adminUsers.detail.saving") : t("adminUsers.detail.save")}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    {t("adminUsers.detail.cancel")}
                  </button>
                </div>
                {updateMutation.isError && (
                  <p className="text-sm text-red-600">
                    {(updateMutation.error as Error).message}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t("adminUsers.detail.firstName")}</h3>
                    <p className="text-foreground">{user.first_name || "—"}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t("adminUsers.detail.lastName")}</h3>
                    <p className="text-foreground">{user.last_name || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{user.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{user.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{user.locale || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground">{user.timezone || "—"}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workspaces */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">Workspaces</h3>
            {userWorkspacesData && (
              <span className="text-sm text-muted-foreground">
                ({userWorkspacesData.workspaces.length})
              </span>
            )}
          </div>

          {isLoadingWorkspaces ? (
            <div className="flex items-center gap-2 p-4 bg-muted rounded-lg text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading workspaces…</span>
            </div>
          ) : !userWorkspacesData || userWorkspacesData.workspaces.length === 0 ? (
            <div className="p-4 bg-muted rounded-lg text-sm text-muted-foreground">
              User is not a member of any workspace.
            </div>
          ) : (
            <div className="overflow-hidden border border-border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Name</th>
                    <th className="text-left px-4 py-2 font-medium">Handle</th>
                    <th className="text-left px-4 py-2 font-medium">Role</th>
                    <th className="text-left px-4 py-2 font-medium">Members</th>
                  </tr>
                </thead>
                <tbody>
                  {userWorkspacesData.workspaces.map((ws) => (
                    <tr key={ws.id} className="border-t border-border hover:bg-accent/50">
                      <td className="px-4 py-2">
                        <Link
                          to={adminRoutes.workspace(ws.id)}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {ws.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2 font-mono text-muted-foreground">
                        {ws.handle ? `@${ws.handle}` : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-block px-2 py-0.5 rounded bg-muted text-foreground text-xs">
                          {ws.role ?? "none"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{ws.member_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Wizard Entries */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Wizard Entries</h3>
          <WizardEntriesList userId={userId!} showWizardColumn />
        </div>

        {/* ── Danger Zone ───────────────────────────────────────────────────────── */}
        <div className="mt-6 border-2 border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Danger Zone</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            These actions are irreversible. Please proceed with extreme caution.
          </p>

          <div className="space-y-4">
            {/* Permanently Delete User */}
            <div className="flex items-start justify-between gap-4 p-4 bg-muted rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-foreground">Permanently Delete User</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Irreversibly deletes the user account, their sole workspace, all GCS files,
                  Stripe/Firestore data, and Firebase Auth account. This cannot be undone.
                </p>

                {/* Eligibility reasons */}
                {eligibilityReasons.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {eligibilityReasons.map((reason, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-sm text-muted-foreground">
                        <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Loading workspace data */}
                {isLoadingWorkspaces && (
                  <div className="flex items-center gap-1.5 mt-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Checking workspace eligibility…</span>
                  </div>
                )}
              </div>

              <button
                onClick={handleOpenPurgeDialog}
                disabled={!isEligibleForPurge || isLoadingWorkspaces}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                <Trash2 className="h-4 w-4" />
                Delete User
              </button>
            </div>
          </div>
        </div>

        {/* ── Purge error banner (shown outside dialog after failure) ──────────── */}
        {purgeError && !purgeMutation.isPending && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800 dark:text-red-200">Purge failed</p>
              <p className="text-sm text-red-600">{purgeError}</p>
            </div>
          </div>
        )}
        </>
      )}

      {/* ── Purge Dialog — Step 1: Warning ───────────────────────────────────── */}
      {purgeDialogStep === 1 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Permanently Delete User</h2>
            </div>

            <div className="space-y-3 mb-6 text-sm text-muted-foreground">
              <p>
                You are about to <strong className="text-foreground">permanently and irreversibly</strong> delete
                this user. The following data will be destroyed:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>User account and profile</li>
                <li>Sole workspace and all projects/documents</li>
                <li>All GCS-stored files</li>
                <li>Stripe customer and billing records</li>
                <li>Firestore boost transactions</li>
                <li>Firebase Authentication account</li>
              </ul>
              <p className="text-red-700 dark:text-red-300 font-medium">
                This action cannot be undone. There is no recovery.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleClosePurgeDialog}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setPurgeDialogStep(2)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Purge Dialog — Step 2: Type email ────────────────────────────────── */}
      {purgeDialogStep === 2 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Confirm User Email</h2>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              To confirm, type the user's email address exactly as it appears in the profile:
            </p>

            <input
              type="text"
              value={purgeEmailInput}
              onChange={(e) => setPurgeEmailInput(e.target.value)}
              placeholder="user@example.com"
              autoComplete="off"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-6"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPurgeDialogStep(1)}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setPurgeDialogStep(3)}
                disabled={!emailMatches}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Purge Dialog — Step 3: Type confirmation phrase ──────────────────── */}
      {purgeDialogStep === 3 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Final Confirmation</h2>
            </div>

            <p className="text-sm text-muted-foreground mb-1">
              Type <span className="font-mono font-semibold text-red-700 dark:text-red-400">DELETE PERMANENTLY</span> to enable the delete button:
            </p>

            <input
              type="text"
              value={purgeConfirmInput}
              onChange={(e) => setPurgeConfirmInput(e.target.value)}
              placeholder="DELETE PERMANENTLY"
              autoComplete="off"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mt-3 mb-6 font-mono"
            />

            {purgeMutation.isError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{purgeMutation.error?.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPurgeDialogStep(2)}
                disabled={purgeMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handlePurgeSubmit}
                disabled={!confirmPhraseMatches || purgeMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purgeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
